/**
 * "Showing the first N of M" line for a picker whose dropdown holds only part
 * of the catalog.
 *
 * A capped page that says nothing about being capped reads as the complete
 * answer, so a row sorting past the cap looks like it doesn't exist — that is
 * how a live customer came to be reported as missing from the timesheet
 * report. Every picker that can truncate should render this, which is why it
 * lives here rather than being re-typed per autocomplete.
 *
 * The "type to narrow" hint is conditional on purpose: in preload mode the
 * search box filters the already-fetched page in memory, so typing cannot
 * reach the very rows this notice is about, and telling the user to type
 * would send them looking for something the dropdown can't produce.
 *
 * Callers wrap this — `Combobox` gives `renderFooter` a bordered container and
 * `EntityPicker` supplies its own — so no border here.
 */
export function PickerTruncationNotice({
  truncated,
  noun = 'matches',
  clientFiltered,
}: {
  /** `truncated` straight off `useEntityAutocomplete`; null renders nothing. */
  truncated: { shown: number; total: number } | null;
  /** Plural entity noun, e.g. "customers". Defaults to a generic "matches"
   *  for pickers that aren't bound to one entity type. */
  noun?: string;
  /** True when filtering is in-memory ('preload' — the hook's `isClient`).
   *  Suppresses the type-to-narrow hint, which only holds for server search. */
  clientFiltered: boolean;
}) {
  if (!truncated) return null;
  return (
    <div className="px-3 py-2 text-xs text-muted-foreground">
      Showing the first {truncated.shown} of {truncated.total} {noun}.
      {clientFiltered ? null : ' Type to narrow the list.'}
    </div>
  );
}
